import {
  Box,
  Typography,
  useTheme,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

const API = "https://ams.arcticvault.io/api/schools";

const StatusChip = ({ status }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const config = {
    Active: {
      icon: <CheckCircleOutlinedIcon sx={{ fontSize: "14px" }} />,
      color: colors.greenAccent[400],
      bg: "#0f2922",
    },
    Pending: {
      icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: "14px" }} />,
      color: "#f0a500",
      bg: "#2a1f00",
    },
    Suspended: {
      icon: <PauseCircleOutlinedIcon sx={{ fontSize: "14px" }} />,
      color: colors.redAccent[400],
      bg: "#2c100f",
    },
    Cancelled: {
      icon: <CancelOutlinedIcon sx={{ fontSize: "14px" }} />,
      color: colors.grey[400],
      bg: colors.grey[800],
    },
  };

  const { icon, color, bg } = config[status] || config["Pending"];

  return (
    <Chip
      icon={icon}
      label={status}
      size="small"
      sx={{
        backgroundColor: bg,
        color: color,
        fontWeight: "bold",
        fontSize: "11px",
        "& .MuiChip-icon": { color: color },
      }}
    />
  );
};

const PlanChip = ({ plan }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const planColors = {
    Starter: colors.blueAccent[400],
    Standard: colors.blueAccent[300],
    Premium: colors.greenAccent[400],
    Enterprise: "#f0a500",
  };

  return (
    <Chip
      label={plan}
      size="small"
      sx={{
        backgroundColor: "transparent",
        border: `1px solid ${planColors[plan] || colors.grey[400]}`,
        color: planColors[plan] || colors.grey[400],
        fontWeight: "bold",
        fontSize: "11px",
      }}
    />
  );
};

const Schools = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSchools = () => {
    setLoading(true);
    fetch(API)
      .then((res) => res.json())
      .then((data) => {
        setSchools(data.map((s) => ({ ...s })));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const columns = [
    {
      field: "school_name",
      headerName: "School Name",
      flex: 1.5,
      renderCell: ({ row }) => (
        <Typography fontWeight="bold" color={colors.grey[100]}>
          {row.school_name}
        </Typography>
      ),
    },
    { field: "location", headerName: "Location", flex: 1 },
    { field: "contact_person", headerName: "Contact Person", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.2 },
    {
      field: "plan",
      headerName: "Plan",
      flex: 0.8,
      renderCell: ({ row }) => <PlanChip plan={row.plan} />,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: ({ row }) => <StatusChip status={row.status} />,
    },
    {
      field: "students_count",
      headerName: "Students",
      flex: 0.6,
      renderCell: ({ row }) => (
        <Typography color={colors.greenAccent[400]}>
          {row.students_count || 0}
        </Typography>
      ),
    },
    {
      field: "teachers_count",
      headerName: "Teachers",
      flex: 0.6,
      renderCell: ({ row }) => (
        <Typography color={colors.blueAccent[300]}>
          {row.teachers_count || 0}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Created",
      flex: 0.9,
      renderCell: ({ row }) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="View School">
          <IconButton
            onClick={() => navigate(`/schools/${row.id}`)}
            sx={{ color: colors.blueAccent[300] }}
          >
            <VisibilityOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="ALL SCHOOLS" subtitle="Manage all school clients on the platform" />
        <Box display="flex" gap="10px">
          <Button
            onClick={fetchSchools}
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            sx={{ borderColor: colors.blueAccent[400], color: colors.blueAccent[300] }}
          >
            Refresh
          </Button>
          <Button
            onClick={() => navigate("/schools/add")}
            variant="contained"
            startIcon={<AddBusinessOutlinedIcon />}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: "#fff",
              fontWeight: "bold",
              "&:hover": { backgroundColor: colors.greenAccent[500] },
            }}
          >
            Add School
          </Button>
        </Box>
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="16px" mt="20px" mb="24px">
        {[
          { label: "Total Schools", value: schools.length, color: colors.blueAccent[300] },
          { label: "Active", value: schools.filter((s) => s.status === "Active").length, color: colors.greenAccent[400] },
          { label: "Pending", value: schools.filter((s) => s.status === "Pending").length, color: "#f0a500" },
          { label: "Suspended", value: schools.filter((s) => s.status === "Suspended").length, color: colors.redAccent[400] },
        ].map((stat) => (
          <Box key={stat.label} p="16px" borderRadius="8px" sx={{ backgroundColor: colors.primary[400] }}>
            <Typography variant="h3" fontWeight="bold" color={stat.color}>{stat.value}</Typography>
            <Typography variant="h6" color={colors.grey[300]}>{stat.label}</Typography>
          </Box>
        ))}
      </Box>

      <Box
        height="55vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
        }}
      >
        <DataGrid rows={schools} columns={columns} loading={loading} getRowId={(row) => row.id} />
      </Box>
    </Box>
  );
};

export default Schools;
